import { Injectable, Type } from "@angular/core";
import { Serializer, PRIMITIVE } from "shared/provider-webworker";

import * as CubiqTS from "./index";

import { CubiqPrototypal } from "./CubiqPrototypal";
import { CubiqPrototypalWrapper } from "./CubiqPrototypalWrapper";

// Wrapper for PROTOTYPAL ctor, twist is needed to support base libraries
// types that are only implicitly PROTOTYPAL & are deserialzed manually:
// BigNumber
// Long
// moment
export const PROTOTYPAL: (type: any) => CubiqTS.CubiqPrototypal = CubiqPrototypalWrapper.wrap;

export const ENCODABLE: (type: any) => CubiqTS.ConcreteEncodable = (
  type: any | any[],
): CubiqTS.ConcreteEncodable | CubiqTS.ConcreteEncodable => {
  if (new type() instanceof CubiqTS.ConcreteEncodable) {
    return new type();
  }
  throw new Error("Attempted to encode unsupported ENCODABLE with CubiqSerializer");
};

export const MIXED_ENCODABLE: (type: any) => CubiqTS.ConcreteEncodable[] = (
  type: any[],
): CubiqTS.ConcreteEncodable[] => {
  if (Array.isArray(type)) {
    return type.map((t) => {
      if (new t() instanceof CubiqTS.ConcreteEncodable) {
        return new t();
      } else {
        throw new Error("Attempted to encode unsupported ENCODABLE with CubiqSerializer");
      }
    });
  }
  throw new Error("Attempted to encode unsupported MIXED_ENCODABLE with CubiqSerializer");
};

@Injectable()
export class CubiqSerializer extends Serializer {
  constructor() {
    super();
  }

  serialize(obj: any, type: any): any {
    if (obj == null) {
      return null;
    }

    if (Array.isArray(obj)) {
      return (<any[]>obj).map((v) => this.serialize(v, type));
    }

    if (type == PRIMITIVE) {
      return obj;
    }

    // structured clone takes care of PROTOTYPAL serialization.
    if (type instanceof CubiqTS.CubiqPrototypal) {
      return obj;
    }

    if (type instanceof CubiqTS.ConcreteEncodable || Array.isArray(type)) {
      let streamout = new CubiqTS.ByteBuffer();

      if (obj === undefined) {
        throw new Error(`${this.constructor.name}: undefined ConcreteEncodable`);
      }

      if ((obj as CubiqTS.ConcreteEncodable).encoder === undefined) {
        throw new Error(
          `${this.constructor.name}: undefined ConcreteEncodable encoder ${obj.encodableType}`,
        );
      }

      if (Array.isArray(type)) {
        let mixedEncodableIndex = type.findIndex((t) => t.encodableType === obj.encodableType);
        if (mixedEncodableIndex === -1) {
          new Error(
            `${this.constructor.name}: mismatched ConcreteEncodable implementation ${obj.encodableType} vs declared type ${type}`,
          );
        }
        streamout.writeUint32(mixedEncodableIndex);
      } else if (obj.encodableType != type.encodableType) {
        throw new Error(
          `${this.constructor.name}: mismatched ConcreteEncodable implementation ${obj.encodableType} vs declared type ${type.encodableType}`,
        );
      }

      (obj as CubiqTS.ConcreteEncodable).encoder.encode(obj, streamout);
      // copy will be zapped
      streamout.flip();
      return streamout.toBuffer(true);
    }

    // no support for Render serialization etc
    throw new Error("No serializer for " + type.toString());
  }

  deserialize(map: any, type: any, data?: any): any {
    if (map == null) {
      return null;
    }

    if (Array.isArray(map)) {
      return (<any[]>map).map((v) => this.deserialize(v, type));
    }

    if (type === PRIMITIVE) {
      return map;
    }

    // restore prototype chain using existing constructor
    if (type instanceof CubiqPrototypalWrapper) {
      // TODO typed access to constructor?
      if (map === undefined) {
        throw new Error(`${this.constructor.name}: undefined PROTOTYPAL encoder.`);
      }

      return type.unwrap(map);
    }

    if (type instanceof CubiqTS.CubiqPrototypal) {
      return new (type as any)(map);
    }

    if (type instanceof CubiqTS.ConcreteEncodable || Array.isArray(type)) {
      // TODO typed access to constructor?
      if (map === undefined) {
        throw new Error(`${this.constructor.name}: undefined ConcreteEncodable passed.`);
      }

      let streamin = CubiqTS.ByteBuffer.wrap(map as ArrayBuffer);
      let encodable;
      let encodableType;
      let mixedEncodableIndex = -1;

      if (Array.isArray(type)) {
        mixedEncodableIndex = streamin.readUint32();
        encodable = new (type[mixedEncodableIndex] as any).constructor();
        encodableType = type[mixedEncodableIndex].encodableType;
      } else {
        encodable = new (type as any).constructor();
        encodableType = type.encodableType;
      }
      encodable.setInnerEncoding(false);

      if (encodable.encoder === undefined) {
        throw new Error(
          `${this.constructor.name}: undefined ConcreteEncodable encoder please fix.`,
        );
      }

      encodable.encoder.decode(encodable, streamin);

      if (encodable.encodableType != encodableType) {
        throw new Error(
          `${this.constructor.name}: mismatched ConcreteEncodable implementation ${encodableType} vs declared type ${type}`,
        );
      }

      return encodable;
    }

    // no support for Render deserialization etc
    throw new Error("No deserializer for " + type.toString());
  }
}
